import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Entity, OutlineEntity, Post } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { EntityTypeCardsProvider, usePosts, useTopics, useView, ViewProvider } from '@shapeable/ui';
import { ExplorerView } from '../../types';
import { partition, sortBy } from 'lodash';
import { useInvitedContributions } from '../../hooks/use-invited-contributions';
import { useLandmarkReports } from '../../hooks/use-landmark-reports';
import { PublicationAnimatedGrid } from './publication-animated-grid';
import { PublicationTypeFilterBar } from './publication-type-filter-bar';
import { PublicationSelectFilters } from './publication-select-filters';
import { TopicPublicationCard } from './topic-publication-card';
import { PagePublicationCard } from './page-publication-card';
import { PostPublicationCard } from './post-publication-card';
import { useSenseMakingPages } from '../../hooks/use-sense-making-pages';
const cls = classNames('page-publication-grid-layout');

// -------- Types -------->

export type PagePublicationGridLayoutProps = Classable & HasChildren & { 
  entity?: Entity;
};

export const PagePublicationGridLayoutDefaultProps: Omit<PagePublicationGridLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(6)};
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(6)};
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
};

const PUBLICATIONS_VIEW_CONFIG = { filterStateVariant: 'state' as const };

const isEditorialPost = (post: Post) => post.type?.slug === 'editorial';

const PagePublicationGridContent: React.FC<PagePublicationGridLayoutProps> = (props) => {
  const { className } = props;

  const allTopics = useTopics();
  const allInvitedContributions = useInvitedContributions();
  const allLandmarkReports = useLandmarkReports();
  const allSenseMakingPages = useSenseMakingPages();
  const allPosts = usePosts();

  const view = 'publications';
  const { filters, resultsFor } = useView<ExplorerView>(view);

  const { topics = [] } = filters;

  const filterItem = React.useCallback((item: OutlineEntity) => {
    const slugMatch = topics.length === 0 || topics.includes(item.slug);
    return slugMatch;
  }, [topics]);

  const filteredTopics = React.useMemo(
    () => allTopics?.filter(filterItem),
    [allTopics, filterItem],
  );
  const filteredInvitedContributions = React.useMemo(
    () => allInvitedContributions?.filter(filterItem),
    [allInvitedContributions, filterItem],
  );
  const filteredLandmarkReports = React.useMemo(
    () => allLandmarkReports?.filter(filterItem),
    [allLandmarkReports, filterItem],
  );
  const filteredSenseMakingPages = React.useMemo(
    () => allSenseMakingPages?.filter(filterItem),
    [allSenseMakingPages, filterItem],
  );
  const filteredPosts = React.useMemo(
    () => allPosts?.filter(filterItem),
    [allPosts, filterItem],
  );

  const sortedEditorialPosts = React.useMemo(
    () => sortBy((filteredPosts || []).filter(isEditorialPost), 'name'),
    [filteredPosts],
  );

  const publicationTypeCounts = React.useMemo(() => {
    const Topic = filteredTopics?.length ?? 0;
    const InvitedContribution = filteredInvitedContributions?.length ?? 0;
    const LandmarkReport = filteredLandmarkReports?.length ?? 0;
    const EditorialPost = sortedEditorialPosts.length;
    const SenseMaker = filteredSenseMakingPages?.length ?? 0;
    return {
      Topic,
      InvitedContribution,
      LandmarkReport,
      SenseMaker,
      EditorialPost,
      all: Topic + InvitedContribution + LandmarkReport + SenseMaker + EditorialPost,
    };
  }, [
    filteredTopics,
    filteredInvitedContributions,
    filteredLandmarkReports,
    filteredSenseMakingPages,
    sortedEditorialPosts,
  ]);

  const gridItems = React.useMemo(() => {
    const toggleItems = [
      ...(resultsFor({ type: 'Topic', data: filteredTopics, by: 'name' })),
      ...(resultsFor({ type: 'InvitedContribution', data: filteredInvitedContributions, by: 'name' })),
      ...(resultsFor({ type: 'LandmarkReport', data: filteredLandmarkReports, by: 'name' })),
      ...(resultsFor({ type: 'SenseMaker', data: filteredSenseMakingPages, by: 'name' })),
      ...(resultsFor({ type: 'EditorialPost', data: sortedEditorialPosts, by: 'name' })),
    ];

    const [dated, undated] = partition(toggleItems, (item) => !!(item as { published?: string }).published);
    return [...sortBy(dated, 'published').reverse(), ...undated];
  }, [
    filteredTopics,
    filteredInvitedContributions,
    filteredLandmarkReports,
    filteredSenseMakingPages,
    sortedEditorialPosts,
    resultsFor,
  ]);

  return (
    <>
      <PublicationTypeFilterBar
        view={view}
        counts={publicationTypeCounts}
        toolbarEnd={<PublicationSelectFilters view={view} />}
      />
      <PublicationAnimatedGrid items={gridItems} />
    </>
  );
};

export const PagePublicationGridLayout: Shapeable.FC<PagePublicationGridLayoutProps> = (props) => {
  const { className, children } = props;

  return (
    <EntityTypeCardsProvider value={{
      Topic: TopicPublicationCard,
      LandmarkReport: PagePublicationCard,
      Page: PagePublicationCard,
      Post: PostPublicationCard,
      InvitedContribution: PagePublicationCard,
      SenseMaker: PagePublicationCard,
    }}>
    <My.Container className={cls.name(className)}>
      <ViewProvider value={PUBLICATIONS_VIEW_CONFIG}>
        <PagePublicationGridContent {...props} />
      </ViewProvider>
    </My.Container>
    </EntityTypeCardsProvider>
  )
};

PagePublicationGridLayout.defaultProps = PagePublicationGridLayoutDefaultProps;
PagePublicationGridLayout.cls = cls;
